import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ScrollArea } from '../../components/ui/scroll-area';
import { 
  User, Phone, Mail, Bell, PhoneCall, UserPlus, Building2, Target,
  FileText, History, Users, ArrowRight, Check, Clock, Plus,
  Calendar as CalendarIcon, ChevronRight, MessageCircle, Send,
  Lightbulb, HelpCircle, Share2, Bot, Sparkles, File, Video, Image as ImageIcon,
  Copy, ExternalLink, ChevronDown, ChevronUp, X
} from 'lucide-react';
import { ACTIVITY_TYPES } from './constants';
import { WhatsAppIcon } from './WhatsAppIcon';

export const LeadDetailView = ({ 
  details, onLogActivity, onCreateReminder, onUpdateDisposition, onReassign, 
  formatDate, formatDateTime, canReassign, getSourceLabel, pipelineStages, getStageConfig,
  collaterals = [],
  salesEnablement = null,
  onShareCollateral,
  onAskEffy,
  effyMessages = [],
  effyLoading = false
}) => {
  const { lead, project, partner, assigned_to, activities, contact_history, disposition_history, contacted_by, stats, reminders, site_visits } = details;
  const stageConfig = getStageConfig(lead?.status);
  const [activeDetailTab, setActiveDetailTab] = useState('overview');
  const [activeSalesKitTab, setActiveSalesKitTab] = useState('pitch');
  const [expandedContact, setExpandedContact] = useState(null);
  const [effyInput, setEffyInput] = useState('');
  const [effyOpen, setEffyOpen] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const effyEndRef = useRef(null);
  
  useEffect(() => {
    effyEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [effyMessages]);
  
  const getNextActionSuggestion = () => {
    const status = lead?.status || 'new';
    const lastContact = contact_history?.[0];
    const daysSinceContact = lastContact ? 
      Math.floor((new Date() - new Date(lastContact.created_at)) / (1000 * 60 * 60 * 24)) : null;
    
    const suggestions = {
      new: { action: 'Make first contact call', type: 'call' },
      contacted: { action: 'Follow up to gauge interest', type: 'call' },
      interested: { action: 'Schedule site visit', type: 'site_visit' },
      site_visit: { action: 'Conduct site visit', type: 'meeting' },
      negotiation: { action: 'Discuss pricing and close deal', type: 'meeting' },
      booked: { action: 'Send booking confirmation', type: 'email' },
      lost: { action: 'Archive or re-engage later', type: 'note' },
    };
    
    let suggestion = suggestions[status] || suggestions.new;
    if (daysSinceContact && daysSinceContact > 3) {
      suggestion = { action: `Follow up (${daysSinceContact} days since last contact)`, type: 'call', urgent: true };
    }
    return suggestion;
  };

  const nextAction = getNextActionSuggestion();
  
  const handleEffySend = () => {
    if (!effyInput.trim() || !onAskEffy) return;
    onAskEffy(effyInput, lead?.id);
    setEffyInput('');
  };
  
  const getCollateralIcon = (type) => {
    switch(type) {
      case 'video': return <Video className="h-4 w-4" />;
      case 'image': return <ImageIcon className="h-4 w-4" />;
      default: return <File className="h-4 w-4" />;
    }
  };
  
  return (
    <div className="flex flex-col h-[80vh]">
      {/* STICKY HEADER - Client Info */}
      <div className="sticky top-0 z-20 bg-white border-b pb-4 -mx-6 px-6 pt-2">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-full bg-indigo-100 flex items-center justify-center">
              <User className="h-7 w-7 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold">{lead?.name}</h2>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <Badge style={{ backgroundColor: stageConfig.color + '20', color: stageConfig.color }}>
                  {stageConfig.label}
                </Badge>
                {partner && <Badge variant="outline">{partner.name}</Badge>}
                {assigned_to && (
                  <Badge variant="secondary" className="text-xs">Assigned: {assigned_to.name}</Badge>
                )}
              </div>
              <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Phone className="h-3 w-3" /> {lead?.phone}
                </span>
                {lead?.email && (
                  <span className="flex items-center gap-1">
                    <Mail className="h-3 w-3" /> {lead?.email}
                  </span>
                )}
              </div>
            </div>
          </div>
          
          {/* Quick Action Buttons */}
          <div className="flex flex-col gap-2">
            <div className="flex gap-2">
              <Button 
                size="sm"
                className="bg-green-600 hover:bg-green-700 text-white"
                onClick={() => window.open(`tel:${lead?.phone}`, '_self')}
                data-testid="detail-call-btn"
              >
                <Phone className="h-4 w-4 mr-1" />
                Call
              </Button>
              <Button 
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={() => window.open(`https://wa.me/${lead?.phone?.replace(/[^0-9]/g, '')}`, '_blank')}
                data-testid="detail-whatsapp-btn"
              >
                <WhatsAppIcon className="h-4 w-4 mr-1" />
                WhatsApp
              </Button>
              {lead?.email && (
                <Button 
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={() => window.open(`mailto:${lead?.email}`, '_self')}
                  data-testid="detail-email-btn"
                >
                  <Mail className="h-4 w-4 mr-1" />
                  Email
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => onLogActivity(lead?.id)} data-testid="detail-log-activity-btn">
                <PhoneCall className="h-4 w-4 mr-1" />
                Log
              </Button>
              <Button variant="outline" size="sm" onClick={() => onCreateReminder(lead?.id)} data-testid="detail-reminder-btn">
                <Bell className="h-4 w-4 mr-1" />
                Reminder
              </Button>
              {canReassign && (
                <Button variant="outline" size="sm" onClick={() => onReassign(lead)} data-testid="detail-reassign-btn">
                  <UserPlus className="h-4 w-4 mr-1" />
                  Reassign
                </Button>
              )}
              <Button size="sm" onClick={() => onUpdateDisposition(lead)} data-testid="detail-update-status-btn">
                Update Status
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* SCROLLABLE CONTENT */}
      <div className="flex-1 overflow-y-auto pt-4 relative">
        {/* Next Action Suggestion */}
        <Card className={`mb-4 ${nextAction.urgent ? 'border-red-300 bg-red-50' : 'border-amber-300 bg-amber-50'}`}>
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`h-9 w-9 rounded-full flex items-center justify-center ${nextAction.urgent ? 'bg-red-100' : 'bg-amber-100'}`}>
                  <Target className={`h-4 w-4 ${nextAction.urgent ? 'text-red-600' : 'text-amber-600'}`} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Suggested Next Action</p>
                  <p className={`font-semibold text-sm ${nextAction.urgent ? 'text-red-700' : 'text-amber-700'}`}>
                    {nextAction.action}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="h-8" onClick={() => onLogActivity(lead?.id)}>
                  <PhoneCall className="h-3 w-3 mr-1" />
                  Log Now
                </Button>
                <Button size="sm" variant="outline" className="h-8" onClick={() => onCreateReminder(lead?.id)}>
                  <Bell className="h-3 w-3 mr-1" />
                  Remind
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-3 mb-4">
          <div className="p-3 bg-blue-50 rounded-lg text-center">
            <p className="text-xl font-bold text-blue-700">{stats?.total_calls || 0}</p>
            <p className="text-xs text-blue-600">Total Calls</p>
          </div>
          <div className="p-3 bg-emerald-50 rounded-lg text-center">
            <p className="text-xl font-bold text-emerald-700">{stats?.total_contacts || 0}</p>
            <p className="text-xs text-emerald-600">Total Contacts</p>
          </div>
          <div className="p-3 bg-amber-50 rounded-lg text-center">
            <p className="text-xl font-bold text-amber-700">{stats?.unique_contactors || 0}</p>
            <p className="text-xs text-amber-600">Contacted By</p>
          </div>
          <div className="p-3 bg-purple-50 rounded-lg text-center">
            <p className="text-xl font-bold text-purple-700">{stats?.total_call_duration_minutes || 0}</p>
            <p className="text-xs text-purple-600">Call Mins</p>
          </div>
        </div>

        {/* Tabs - Remove Ask Effy from here */}
        <Tabs value={activeDetailTab} onValueChange={setActiveDetailTab}>
          <TabsList className="grid grid-cols-4 w-full">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="contacts">Contacts</TabsTrigger>
            <TabsTrigger value="sales-kit" data-testid="sales-kit-tab">
              <Lightbulb className="h-4 w-4 mr-1" />
              Sales Kit
            </TabsTrigger>
            <TabsTrigger value="collaterals" data-testid="collaterals-tab">
              <Share2 className="h-4 w-4 mr-1" />
              Share
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4 mt-4">
            <div className="grid md:grid-cols-2 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Contact Info</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <p className="flex items-center gap-2 text-sm"><Phone className="h-4 w-4 text-muted-foreground" />{lead?.phone}</p>
                  {lead?.email && <p className="flex items-center gap-2 text-sm"><Mail className="h-4 w-4 text-muted-foreground" />{lead?.email}</p>}
                  <p className="flex items-center gap-2 text-sm"><Building2 className="h-4 w-4 text-muted-foreground" />{project?.name || 'No project'}</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Lead Info</CardTitle>
                </CardHeader>
                <CardContent className="space-y-1 text-sm">
                  <p><strong>BHK:</strong> {lead?.bhk_preference || '-'}</p>
                  <p><strong>Budget:</strong> {lead?.budget ? `₹${(lead.budget/100000).toFixed(1)}L` : '-'}</p>
                  <p><strong>Source:</strong> {getSourceLabel(lead?.source)}</p>
                  <p><strong>Created:</strong> {formatDate(lead?.created_at)}</p>
                </CardContent>
              </Card>
            </div>

            {/* Status Journey */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <History className="h-4 w-4" />
                  Status Journey
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2 flex-wrap mb-4">
                  {pipelineStages?.slice(0, 6).map((stage, idx) => {
                    const isCompleted = disposition_history?.some(d => d.title?.toLowerCase().includes(stage.slug));
                    const isCurrent = lead?.status === stage.slug;
                    return (
                      <div key={stage.slug} className="flex items-center">
                        <div 
                          className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${isCurrent ? 'ring-2 ring-offset-2' : ''}`}
                          style={{ 
                            backgroundColor: isCompleted || isCurrent ? stage.color + '30' : '#f1f5f9',
                            color: isCompleted || isCurrent ? stage.color : '#94a3b8',
                            ringColor: isCurrent ? stage.color : 'transparent'
                          }}
                        >
                          {stage.name}
                        </div>
                        {idx < 5 && <ArrowRight className="h-4 w-4 text-slate-300 mx-1" />}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {contacted_by?.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Contacted By
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {contacted_by.map((person, idx) => (
                      <Badge key={idx} variant="secondary" className="py-1 px-3">
                        <User className="h-3 w-3 mr-1" />
                        {person.name}
                        <span className="text-xs ml-1 opacity-70">({person.role})</span>
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {reminders?.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-amber-600 flex items-center gap-2">
                    <Bell className="h-4 w-4" />
                    Pending Reminders ({reminders.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {reminders.map((r, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-amber-50 rounded-lg">
                        <span className="text-sm">{r.title}</span>
                        <Badge variant="outline">{formatDate(r.due_date)}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Contact History Tab */}
          <TabsContent value="contacts" className="space-y-4 mt-4">
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <PhoneCall className="h-4 w-4" />
                    Contact History ({contact_history?.length || 0})
                  </CardTitle>
                  <Button size="sm" onClick={() => onLogActivity(lead?.id)}>
                    <Plus className="h-4 w-4 mr-1" />
                    Log New
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {contact_history?.length === 0 ? (
                  <div className="text-center py-8">
                    <PhoneCall className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground mb-4">No contacts logged yet</p>
                    <Button onClick={() => onLogActivity(lead?.id)}>
                      <Plus className="h-4 w-4 mr-1" />
                      Log First Contact
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[300px] overflow-y-auto">
                    {contact_history?.map((contact, idx) => {
                      const activityType = ACTIVITY_TYPES.find(t => t.value === contact.activity_type);
                      const Icon = activityType?.icon || PhoneCall;
                      const isExpanded = expandedContact === idx;
                      
                      return (
                        <div key={idx} className={`border rounded-lg transition-all ${isExpanded ? 'shadow-md' : 'hover:shadow-sm'}`}>
                          <div className="p-3 cursor-pointer" onClick={() => setExpandedContact(isExpanded ? null : idx)}>
                            <div className="flex items-start gap-3">
                              <div className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 ${
                                contact.outcome === 'interested' ? 'bg-emerald-100' :
                                contact.outcome === 'not_interested' ? 'bg-red-100' : 'bg-slate-100'
                              }`}>
                                <Icon className={`h-5 w-5 ${
                                  contact.outcome === 'interested' ? 'text-emerald-600' :
                                  contact.outcome === 'not_interested' ? 'text-red-600' : 'text-slate-600'
                                }`} />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center justify-between">
                                  <p className="font-medium text-sm">{contact.title}</p>
                                  <p className="text-xs text-muted-foreground">{formatDate(contact.created_at)}</p>
                                </div>
                                <p className="text-xs text-muted-foreground">{activityType?.label} • By {contact.user?.name}</p>
                                <div className="flex items-center gap-2 mt-1">
                                  {contact.outcome && (
                                    <Badge className={`text-xs ${
                                      contact.outcome === 'interested' ? 'bg-emerald-100 text-emerald-700' :
                                      contact.outcome === 'not_interested' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-700'
                                    }`}>
                                      {contact.outcome.replace(/_/g, ' ')}
                                    </Badge>
                                  )}
                                  <ChevronRight className={`h-4 w-4 text-muted-foreground transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                </div>
                              </div>
                            </div>
                          </div>
                          {isExpanded && contact.description && (
                            <div className="px-3 pb-3 border-t bg-slate-50">
                              <p className="text-xs text-muted-foreground pt-2">Notes:</p>
                              <p className="text-sm">{contact.description}</p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Sales Kit Tab with Sub-Tabs */}
          <TabsContent value="sales-kit" className="space-y-4 mt-4">
            {!salesEnablement?.exists ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <Lightbulb className="h-12 w-12 text-muted-foreground mb-4" />
                  <p className="text-lg font-medium mb-2">No Sales Kit Available</p>
                  <p className="text-sm text-muted-foreground text-center">
                    Ask your admin to create elevator pitch and FAQs for {project?.name || 'this project'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-4">
                  {/* Sub-tabs inside Sales Kit */}
                  <Tabs value={activeSalesKitTab} onValueChange={setActiveSalesKitTab}>
                    <TabsList className="grid grid-cols-4 w-full mb-4">
                      <TabsTrigger value="pitch" className="text-xs">
                        <Sparkles className="h-3 w-3 mr-1" />
                        Pitch
                      </TabsTrigger>
                      <TabsTrigger value="highlights" className="text-xs">
                        <Target className="h-3 w-3 mr-1" />
                        Highlights
                      </TabsTrigger>
                      <TabsTrigger value="faqs" className="text-xs">
                        <HelpCircle className="h-3 w-3 mr-1" />
                        FAQs
                      </TabsTrigger>
                      <TabsTrigger value="objections" className="text-xs">
                        <MessageCircle className="h-3 w-3 mr-1" />
                        Objections
                      </TabsTrigger>
                    </TabsList>

                    {/* Elevator Pitch Sub-Tab */}
                    <TabsContent value="pitch">
                      <div className="border-l-4 border-indigo-500 bg-indigo-50 p-4 rounded-r-lg">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-indigo-700 flex items-center gap-2">
                            <Sparkles className="h-4 w-4" />
                            Elevator Pitch
                          </h4>
                          <Button variant="ghost" size="sm" className="h-7" onClick={() => {
                            navigator.clipboard.writeText(salesEnablement.elevator_pitch);
                          }}>
                            <Copy className="h-3 w-3 mr-1" />
                            Copy
                          </Button>
                        </div>
                        <p className="text-sm leading-relaxed whitespace-pre-wrap">
                          {salesEnablement.elevator_pitch || "No elevator pitch defined yet."}
                        </p>
                      </div>
                    </TabsContent>

                    {/* Key Highlights Sub-Tab */}
                    <TabsContent value="highlights">
                      {salesEnablement.key_highlights?.length > 0 ? (
                        <div className="space-y-2">
                          {salesEnablement.key_highlights.map((h, idx) => (
                            <div key={idx} className="flex items-start gap-2 p-2 bg-emerald-50 rounded-lg">
                              <Check className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                              <span className="text-sm">{h}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground text-center py-8">No key highlights defined</p>
                      )}
                    </TabsContent>

                    {/* FAQs Sub-Tab */}
                    <TabsContent value="faqs">
                      {salesEnablement.faqs?.length > 0 ? (
                        <div className="space-y-2">
                          {salesEnablement.faqs.map((faq, idx) => (
                            <div key={idx} className="border rounded-lg overflow-hidden">
                              <button
                                className="w-full px-4 py-3 text-left flex items-center justify-between hover:bg-slate-50"
                                onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                              >
                                <span className="font-medium text-sm">{faq.question}</span>
                                <ChevronRight className={`h-4 w-4 text-muted-foreground transition-transform ${expandedFaq === idx ? 'rotate-90' : ''}`} />
                              </button>
                              {expandedFaq === idx && (
                                <div className="px-4 py-3 bg-slate-50 border-t">
                                  <p className="text-sm text-muted-foreground">{faq.answer}</p>
                                  <Button variant="ghost" size="sm" className="mt-2 h-6" onClick={() => {
                                    navigator.clipboard.writeText(faq.answer);
                                  }}>
                                    <Copy className="h-3 w-3 mr-1" />
                                    Copy
                                  </Button>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground text-center py-8">No FAQs defined</p>
                      )}
                    </TabsContent>

                    {/* Objection Handlers Sub-Tab */}
                    <TabsContent value="objections">
                      {salesEnablement.objection_handlers?.length > 0 ? (
                        <div className="space-y-3">
                          {salesEnablement.objection_handlers.map((oh, idx) => (
                            <div key={idx} className="bg-amber-50 rounded-lg p-3 border-l-4 border-amber-500">
                              <p className="text-sm font-medium text-amber-800">"{oh.objection}"</p>
                              <p className="text-sm text-amber-700 mt-1">→ {oh.response}</p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground text-center py-8">No objection handlers defined</p>
                      )}
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Share Collaterals Tab */}
          <TabsContent value="collaterals" className="space-y-4 mt-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Share2 className="h-4 w-4" />
                  Share Collaterals with {lead?.name}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {collaterals?.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No collaterals available</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {collaterals.map((collateral) => (
                      <div key={collateral.id} className="flex items-center gap-4 p-3 border rounded-lg hover:bg-slate-50">
                        <div className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${
                          collateral.type === 'video' ? 'bg-red-100' :
                          collateral.type === 'image' ? 'bg-blue-100' : 'bg-slate-100'
                        }`}>
                          {getCollateralIcon(collateral.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{collateral.title}</p>
                          <p className="text-xs text-muted-foreground capitalize">{collateral.type}</p>
                          {collateral.share_history?.length > 0 && (
                            <p className="text-xs text-emerald-600">Shared {collateral.share_history.length}x</p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            size="sm" variant="outline"
                            className="bg-emerald-50 border-emerald-200 hover:bg-emerald-100 h-8 w-8 p-0"
                            onClick={() => onShareCollateral && onShareCollateral(lead.id, collateral.id, 'whatsapp')}
                          >
                            <WhatsAppIcon className="h-4 w-4 text-emerald-600" />
                          </Button>
                          {lead?.email && (
                            <Button 
                              size="sm" variant="outline"
                              className="bg-blue-50 border-blue-200 hover:bg-blue-100 h-8 w-8 p-0"
                              onClick={() => onShareCollateral && onShareCollateral(lead.id, collateral.id, 'email')}
                            >
                              <Mail className="h-4 w-4 text-blue-600" />
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => window.open(collateral.url, '_blank')}>
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* FLOATING ASK EFFY WIDGET */}
        {effyOpen && (
          <div className="absolute bottom-4 right-4 w-80 bg-white rounded-2xl shadow-2xl border flex flex-col z-30 max-h-[400px]">
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-3 rounded-t-2xl flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bot className="h-5 w-5" />
                <div>
                  <h3 className="font-semibold text-sm">Ask Effy</h3>
                  <p className="text-xs text-white/80">About {lead?.name}</p>
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setEffyOpen(false)} className="text-white hover:bg-white/20 h-7 w-7">
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            {/* Messages */}
            <ScrollArea className="flex-1 p-3 max-h-[250px]">
              {effyMessages.length === 0 ? (
                <div className="text-center py-4">
                  <Sparkles className="h-8 w-8 text-indigo-300 mx-auto mb-2" />
                  <p className="text-xs text-muted-foreground mb-2">Ask for lead-specific suggestions</p>
                  <div className="space-y-1">
                    <Button variant="outline" size="sm" className="w-full text-xs h-7 justify-start"
                      onClick={() => setEffyInput(`Best approach for ${lead?.name}?`)}>
                      Best approach for this lead?
                    </Button>
                    <Button variant="outline" size="sm" className="w-full text-xs h-7 justify-start"
                      onClick={() => setEffyInput('Talking points for the call')}>
                      Talking points for call
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {effyMessages.map((msg, idx) => (
                    <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[85%] p-2 rounded-xl text-xs ${
                        msg.role === 'user' 
                          ? 'bg-indigo-600 text-white rounded-br-sm' 
                          : 'bg-slate-100 text-slate-800 rounded-bl-sm'
                      }`}>
                        <p className="whitespace-pre-wrap">{msg.content}</p>
                      </div>
                    </div>
                  ))}
                  {effyLoading && (
                    <div className="flex justify-start">
                      <div className="bg-slate-100 p-2 rounded-xl rounded-bl-sm flex gap-1">
                        <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  )}
                  <div ref={effyEndRef} />
                </div>
              )}
            </ScrollArea>
            
            {/* Input */}
            <div className="p-2 border-t">
              <div className="flex gap-2">
                <Input 
                  value={effyInput}
                  onChange={(e) => setEffyInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && !effyLoading && handleEffySend()}
                  placeholder="Ask about this lead..."
                  className="flex-1 h-8 text-sm"
                  disabled={effyLoading}
                />
                <Button onClick={handleEffySend} disabled={effyLoading || !effyInput.trim()} size="icon" className="h-8 w-8">
                  <Send className="h-3 w-3" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* ASK EFFY FLOATING BUTTON */}
        {!effyOpen && (
          <button
            onClick={() => setEffyOpen(true)}
            className="absolute bottom-4 right-4 h-12 w-12 rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg hover:shadow-xl transition-all flex items-center justify-center z-30"
            data-testid="ask-effy-floating-btn"
          >
            <Bot className="h-6 w-6" />
          </button>
        )}
      </div>
    </div>
  );
};
